import React from 'react';
import { useOptionalFilterState } from '@/hooks/useFilter';
import { useAuthState } from '@/utils/auth';
import { atom, PrimitiveAtom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Project,
  RoleFieldsForUseProjectOptionsFragment,
  RoleNames,
  useGetAllCompaniesForProjectOptionsQuery,
  useGetRolesWithNestedProjectsQuery,
} from '@/generated/codegen';

export class ProjectNotFoundError extends Error {
  constructor(project_name: string) {
    super(project_name);
    this.name = 'ProjectNotFoundError';
    this.message = `${project_name} was not found in the current logged in user's roles. You might not be allowed to access this project.`;
  }
}

export class BusinessUnitNotFoundError extends Error {
  constructor(business_unit_name: string) {
    super(business_unit_name);
    this.name = 'BusinessUnitNotFoundError';
    this.message = `${business_unit_name} was not found in the current logged in user's roles. You might not be allowed to access this business unit.`;
  }
}

export type ProjectOption = {
  label: string;
  value: {
    company_id: number;
    business_unit_id?: number;
    project_id?: number;
  };
  names: {
    company: string;
    business_unit?: string;
    project?: string;
  };
};

export type NestedOptions = {
  id: number; //company_id
  label: string; //company name
  options: readonly ProjectOption[]; //all options for this company
};

type InferAtom<T extends PrimitiveAtom<any>> =
  T extends PrimitiveAtom<infer K> ? K : never;

export type BusinessUnitOption = {
  id: number;
  name: string;
  slug: string;
  company_id: number;
  /**
   * Whether the user's roles reach the business unit itself, and so can use it
   * as a context. False when the business unit is only known because a project
   * inside it is reachable — then it is a grouping label, not a destination.
   */
  selectable: boolean;
};

const projectsAtom = atom<
  (Pick<Project, 'id' | 'name' | 'slug'> & {
    company_id: number;
    business_unit: { id: number; name: string; slug: string };
  })[]
>([]);
const projectOptionsAtom = atom<NestedOptions[]>([]);
const businessUnitsAtom = atom<BusinessUnitOption[]>([]);
const loadingAtom = atom<boolean>(true);

export const useAllProjectOptions = () => useAtomValue(projectOptionsAtom);

export const useNestedProjectOptions = () => {
  const filter = useOptionalFilterState();
  const allProjectOptions = useAtomValue(projectOptionsAtom);
  const result = [
    allProjectOptions.find(
      (options) => options.id === filter.query?.company_id
    ),
  ].filter((option): option is NestedOptions => !!option);
  return result as Readonly<typeof result>;
};

export const useProjectOptions = () =>
  useNestedProjectOptions()[0]?.options ?? [];

export const useProjectIdToSlug = () => {
  const projects = useAtomValue(projectsAtom);
  return React.useCallback(
    (project_id: number) => {
      const project = projects.find((project) => project.id === project_id);
      if (!project) throw new ProjectNotFoundError(project_id + '');
      return project.slug;
    },
    [projects]
  );
};

export const useProjectSlugToName = () => {
  const [projects] = useAtom(projectsAtom);
  return React.useCallback(
    (slug: string, companyId: number) => {
      const project = projects.find(
        (project) => project.slug === slug && project.company_id === companyId
      );
      if (!project) throw new ProjectNotFoundError(slug);
      return project.name;
    },
    [projects]
  );
};

export const useProjectNameToId = () => {
  const [projects] = useAtom(projectsAtom);
  return React.useCallback(
    (name: string, companyId: number) => {
      const project = projects.find(
        (project) => project.name === name && project.company_id === companyId
      );
      if (!project) throw new ProjectNotFoundError(name);
      return project.id;
    },
    [projects]
  );
};

export const useProjectIdToBusinessUnit = () => {
  const projects = useAtomValue(projectsAtom);
  return React.useCallback(
    (project_id: number) => {
      const project = projects.find((project) => project.id === project_id);
      if (!project) throw new ProjectNotFoundError(project_id + '');
      return project.business_unit;
    },
    [projects]
  );
};

export const useBusinessUnits = () => useAtomValue(businessUnitsAtom);

export const useBusinessUnitOptions = (): BusinessUnitOption[] => {
  const filter = useOptionalFilterState();
  const businessUnits = useAtomValue(businessUnitsAtom);
  const company_id = filter.value.company_id;
  return React.useMemo(
    () =>
      company_id
        ? businessUnits.filter((bu) => bu.company_id === company_id)
        : [],
    [businessUnits, company_id]
  );
};

export const useBusinessUnitIdToSlug = () => {
  const businessUnits = useAtomValue(businessUnitsAtom);
  return React.useCallback(
    (business_unit_id: number) => {
      const businessUnit = businessUnits.find(
        (businessUnit) => businessUnit.id === business_unit_id
      );
      if (!businessUnit)
        throw new BusinessUnitNotFoundError(business_unit_id + '');
      return businessUnit.slug;
    },
    [businessUnits]
  );
};

export const useBusinessUnitSlugToId = () => {
  const businessUnits = useAtomValue(businessUnitsAtom);
  return React.useCallback(
    (slug: string, companyId: number) => {
      const businessUnit = businessUnits.find(
        (businessUnit) =>
          businessUnit.slug === slug && businessUnit.company_id === companyId
      );
      if (!businessUnit) throw new BusinessUnitNotFoundError(slug);
      return businessUnit.id;
    },
    [businessUnits]
  );
};

export const useProjectsLoading = () => useAtomValue(loadingAtom);

type RoleCompany = NonNullable<
  RoleFieldsForUseProjectOptionsFragment['company']
>;
type RoleProject = RoleCompany['projects'][number];

const projectOption = (
  company: RoleCompany,
  project: RoleProject
): ProjectOption => ({
  label: project.name,
  value: {
    company_id: company.id,
    business_unit_id: project.business_unit.id,
    project_id: project.id,
  },
  names: {
    company: company.name,
    business_unit: project.business_unit.name,
    project: project.name,
  },
});

const byProjectId = (
  company: RoleCompany,
  projects: readonly RoleProject[]
): Record<string, ProjectOption> =>
  Object.fromEntries(
    projects.map((project) => [project.id, projectOption(company, project)])
  );

/**
 * The options a single role grants, keyed so roles overlapping on the same
 * project collapse into one option. `company.projects` always lists the whole
 * company, so each scope level has to narrow it down itself (BIL-4887).
 */
const optionsForRole = (
  role: RoleFieldsForUseProjectOptionsFragment
): Record<string, ProjectOption> => {
  const company = role.company;
  if (!company) return {};
  // project-level: only its own project
  if (role.project) return byProjectId(company, [role.project]);
  // business-unit-level: only the projects inside that business unit
  if (role.business_unit) {
    const business_unit_id = role.business_unit.id;
    return byProjectId(
      company,
      company.projects.filter(
        (project) => project.business_unit.id === business_unit_id
      )
    );
  }
  // company-level: an 'all projects' option, plus every project in the company
  return {
    all: {
      label: 'Todos los Proyectos',
      value: { company_id: company.id },
      names: { company: company.name },
    },
    ...byProjectId(company, company.projects),
  };
};

export const ManageProjectOptions: React.FC<{}> = () => {
  const { user } = useAuthState();
  const { data: projectsData } = useGetRolesWithNestedProjectsQuery({
    skip: !user,
  });

  // This is for a superRoot user... in that case the company info does not come in the roles
  // array, so we have to get it ourselves
  const { data: allCompaniesDataForSuperRoot } =
    useGetAllCompaniesForProjectOptionsQuery({
      skip: !user?.roles.some((role) => role?.role === RoleNames.SuperRoot),
    });
  const setProjectOptions = useSetAtom(projectOptionsAtom);
  const setLoading = useSetAtom(loadingAtom);
  const setProjects = useSetAtom(projectsAtom);
  const setBusinessUnits = useSetAtom(businessUnitsAtom);

  React.useEffect(() => {
    if (!user) {
      setProjects([]);
      setProjectOptions([]);
      setBusinessUnits([]);
      return;
    }
    if (projectsData?.getMe?.__typename !== 'GetMe') return;
    const roles: RoleFieldsForUseProjectOptionsFragment[] = [
      ...(projectsData?.getMe?.item?.roles.filter(
        (role): role is RoleFieldsForUseProjectOptionsFragment => !!role
      ) ?? []),
      ...(allCompaniesDataForSuperRoot?.getAllCompanies?.items?.map(
        (company) => ({
          __typename: 'Role' as const,
          id: -1,
          role: RoleNames.SuperRoot,
          company: company,
        })
      ) ?? []),
    ];
    let projects: Record<any, InferAtom<typeof projectsAtom>[number]> = {};
    let businessUnits: Record<any, BusinessUnitOption> = {};
    let companiesOptions: Record<
      any,
      { id: number; label: string; options: Record<any, ProjectOption> }
    > = {};
    // roles overlap, so a business unit reached by several of them keeps the
    // widest reach any single role grants
    const reachBusinessUnit = (
      businessUnit: { id: number; name: string; slug: string },
      company_id: number,
      selectable: boolean
    ) => {
      businessUnits[businessUnit.id] = {
        id: businessUnit.id,
        name: businessUnit.name,
        slug: businessUnit.slug,
        company_id,
        selectable:
          selectable || (businessUnits[businessUnit.id]?.selectable ?? false),
      };
    };

    for (const role of roles) {
      if (!role || !role.company) continue;
      // construct Set of unique projects
      for (const project of role.company?.projects ?? []) {
        if (!project) continue;
        projects[project.id] = { ...project, company_id: role.company.id };
        // company.projects always lists the whole company regardless of the
        // role's scope, so only a company-level role may widen the business
        // unit list to every business unit in it (BIL-4887).
        if (project.business_unit && !role.project && !role.business_unit) {
          reachBusinessUnit(project.business_unit, role.company.id, true);
        }
      }
      // a business-unit-level role reaches its own business unit, and it can
      // be used as a context
      if (role.business_unit) {
        reachBusinessUnit(role.business_unit, role.company.id, true);
      }
      // a project-level role only reaches the business unit containing it, and
      // only as a grouping label
      if (role.project?.business_unit) {
        reachBusinessUnit(role.project.business_unit, role.company.id, false);
      }

      // massage the data into an object of options for each company
      companiesOptions[role.company.id] = {
        id: role.company.id,
        label: role.company.name,
        options: {
          ...companiesOptions[role.company.id]?.options,
          ...optionsForRole(role),
        },
      };
    }

    setProjects(Object.values(projects));
    setBusinessUnits(Object.values(businessUnits));

    setProjectOptions(
      Object.values(companiesOptions).map((companyOptions) => {
        // we remove the "all" option, so we can place it at the top
        const { all, ...options } = companyOptions.options;
        return {
          ...companyOptions,
          // we used an object to deduplicate project options, so now we need to transform back to array
          // since we're at it, move "all projects" to the top cos it looks better
          options: [...(all ? [all] : []), ...Object.values(options)],
        };
      })
    );

    setLoading(false);
  }, [
    allCompaniesDataForSuperRoot?.getAllCompanies?.items,
    projectsData?.getMe,
    setBusinessUnits,
    setLoading,
    setProjectOptions,
    setProjects,
    user,
  ]);

  return null;
};
